<template>
  <div v-if="benefits.length > 0" :class="['benefit-box', theme]">
    <template v-if="theme === 'green'">
      <div v-if="title || subtitle" class="header">
        <div class="box-title">
          {{ title }}
        </div>
        <div v-if="subtitle" class="box-subtitle">
          {{ subtitle }}
        </div>
      </div>
      <ul class="benefit-list">
        <li class="benefit" v-for="benefit in benefits" :key="benefit.content.id">
          <div
            :class="[
              'benefit-icon',
              benefit.content.properties.benefitApplies ? 'benefit-applies' : 'benefit-applies-not',
            ]"
          >
            <svg
              v-if="benefit.content.properties.benefitApplies"
              :aria-label="d['Salary.BenefitBox.AriaLabelBenefitApplies']?.item2"
            >
              <use :href="`${spritemap}#svg-check-mini`" aria-hidden="true"></use>
            </svg>
            <svg v-else :aria-label="d['Salary.BenefitBox.AriaLabelBenefitDoesNotApply']?.item2">
              <use :href="`${spritemap}#svg-x`" aria-hidden="true"></use>
            </svg>
          </div>
          <div>{{ benefit.content.properties.benefit }}</div>
        </li>
      </ul>
    </template>
    <template v-else>
      <div class="box-header-content">
        <div v-if="title || subtitle" class="header">
          <div class="box-title">
            {{ title }}
          </div>
          <div v-if="subtitle" class="box-subtitle">
            {{ subtitle }}
          </div>
        </div>
        <ul class="benefit-list">
          <li class="benefit" v-for="benefit in benefits" :key="benefit.content.id">
            <div
              :class="[
                'benefit-icon',
                benefit.content.properties.benefitApplies
                  ? 'benefit-applies'
                  : 'benefit-applies-not',
              ]"
            >
              <svg
                v-if="benefit.content.properties.benefitApplies"
                :aria-label="d['Salary.BenefitBox.AriaLabelBenefitApplies']?.item2"
              >
                <use :href="`${spritemap}#svg-check-mini`" aria-hidden="true"></use>
              </svg>
              <svg v-else :aria-label="d['Salary.BenefitBox.AriaLabelBenefitDoesNotApply']?.item2">
                <use :href="`${spritemap}#svg-x`" aria-hidden="true"></use>
              </svg>
            </div>
            <div>{{ benefit.content.properties.benefit }}</div>
          </li>
        </ul>
      </div>
    </template>
    <button v-if="startsQuestions" class="button" @click="start()">
      {{ d['Salary.BenefitBox.StartButtonText']?.item2 }}
    </button>
    <a v-if="link" class="button" :href="getLinkUrl(link)" :target="link.target">
      {{ getLinkText(link) }}
    </a>
  </div>
</template>

<script>
import { state, localDictionary } from '@/lib/state'
import { svgSpritemapSrc } from '@/lib/mapping'
import { isDevelopment } from '@/services/environment-service'
import { getCurrentInstance } from 'vue'

export default {
  props: {
    benefits: {
      type: Array,
      required: false,
    },
    title: {
      type: String,
      required: false,
    },
    subtitle: {
      type: String,
      required: false,
    },
    theme: {
      type: String,
      required: false,
    },
    requiresLogin: {
      type: Boolean,
      required: false,
      default: false,
    },
    startsQuestions: {
      type: Boolean,
      required: false,
      default: false,
    },
    link: {
      type: Object,
      required: false,
    },
  },
  data() {
    return {
      spritemap: svgSpritemapSrc,
      d: localDictionary(),
    }
  },
  setup() {
    const instance = getCurrentInstance();
    return {
      emitter: instance?.appContext.config.globalProperties.emitter
    };
  },
  methods: {
    async start() {
      const userAuthenticated = state.userAuthenticated

      if (this.requiresLogin) {
        if (!userAuthenticated && !isDevelopment()) {
          this.triggerMitIdLogin()
        } else {
          // User is already authenticated - set segmentation for members
          const segType = 'member'
          const eventType = "subscriptiontype-" + segType
          
          // Dispatch the segmentation events
          window.dispatchEvent(new CustomEvent("segmentation-reset"))
          window.dispatchEvent(new CustomEvent(eventType))
          
          // Update URL parameter without reloading the page
          const url = new URL(window.location)
          const params = new URLSearchParams(url.search)
          params.set("seg", segType)
          url.search = params.toString()
          history.replaceState(null, "", url.toString())
          
          // Start the questions
          this.emitter.emit('start-questions')
        }
      } else {
        this.$emit('start-lead-popup')
      }
    },
    async triggerMitIdLogin() {
      // Before redirecting to login, add the member segmentation to the URL
      // so when the user returns after login, the correct segmentation is applied
      const url = new URL(window.location)
      const params = new URLSearchParams(url.search)
      
      // Set the segmentation parameter to indicate member status after login
      params.set("seg", "member")
      url.search = params.toString()
      
      // Redirect to login with the updated return URL
      window.location.href =
        '/umbraco/api/MitId/ExternalLogin?provider=OpenIdConnect&returnUrl=' +
        encodeURIComponent(url.toString())
    },
    getLinkUrl(link) {
      return link.route?.path || link.url
    },
    getLinkText(link) {
      return link.title || link.url
    },
  },
}
</script>
<style scoped lang="postcss">
.benefit-box {
  display: flex;
  flex-direction: column;
  gap: 24px;
  border-radius: 5px;
  background-color: var(--color-grey-4);
  padding: 24px 12px;
  justify-content: space-between;
  font-family: var(--font-it-ex);
  font-size: var(--font-size-body);

  .box-header-content {
    display: flex;
    flex-direction: column;
    gap: 24px;
  }

  &.blue {
    background-color: var(--color-blue);
    color: var(--color-white);
  }

  &.green {
    background-color: var(--color-green);
    color: var(--color-darkblue);

    .box-title {
      padding-bottom: 8px;
    }
  }

  .box-title {
    font-size: 20px;
    font-weight: 700;
    line-height: 24px;
  }

  .box-subtitle {
    font-size: var(--font-size-body);
    font-weight: var(--font-weight-normal);
    line-height: 20px;
  }

  .benefit-icon {
    display: flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    width: 16px;
    height: 16px;
    border-radius: 9999px;
    color: var(--color-darkblue);

    svg {
      flex-shrink: 0;
      width: 8px;
      height: 8px;
    }

    &.benefit-applies-not {
      background-color: var(--color-red);
    }

    &.benefit-applies {
      background-color: var(--color-green);
    }
  }
}

.benefit-box.green .benefit-icon.benefit-applies-not,
.benefit-box.green .benefit-icon.benefit-applies {
  background-color: var(--color-darkblue);
  color: var(--color-green);
}

.benefit-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
  /* overwrite default ul styles */
  padding-left: 0;
  margin-bottom: 0;
}

.benefit {
  display: flex;
  align-items: center;
  gap: 12px;
}

.benefit-box .button {
  align-self: center;
  width: fit-content;
}

@media (min-width: 1024px) {
  .benefit-box {
    gap: 40px;
    padding: 28px;
    border-radius: 18px;

    .box-header-content {
      display: flex;
      flex-direction: column;
      gap: 24px;
    }

    .box-title {
      font-size: 24px;
      line-height: 28px;
    }

    .box-subtitle,
    .benefit {
      font-size: 20px;
      line-height: 24px;
    }

    .benefit-list {
      gap: 12px;
    }
  }
}
</style>
